import React, { Fragment, useCallback, useMemo, useState } from "react";

import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import useGetClient from "@fartherfinance/frontend/api/Entity/hooks/useGetClient";
import useDeleteTransitionProposal from "@fartherfinance/frontend/api/TransitionProposals/hooks/useDeleteTransitionProposal";
import useGetTransitionProposals from "@fartherfinance/frontend/api/TransitionProposals/hooks/useGetTransitionProposals";
import usePatchTransitionProposal from "@fartherfinance/frontend/api/TransitionProposals/hooks/usePatchTransitionProposal";
import usePostDuplicateTransitionProposal from "@fartherfinance/frontend/api/TransitionProposals/hooks/usePostDuplicateTransitionProposal";
import { usePostShareTransitionProposal } from "@fartherfinance/frontend/api/TransitionProposals/hooks/usePostShareTransitionProposal";
import usePostTransitionProposal from "@fartherfinance/frontend/api/TransitionProposals/hooks/usePostTransitionProposal";
import type {
  TransitionProposal,
  TransitionProposalId,
  TransitionProposalsImportTypeKey,
} from "@fartherfinance/frontend/api/TransitionProposals/types";
import {
  TransitionProposalsImportTypeMap,
  TransitionProposalsStatusMap,
} from "@fartherfinance/frontend/api/TransitionProposals/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { useGlobalPollingContext } from "@src/multiCustodian/providers/GlobalPolling/GlobalPollingProvider";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import CardButton from "@src/sharedComponents/CardButton/CardButton";
import Chip from "@src/sharedComponents/Chip/Chip";
import DeleteItemModal from "@src/sharedComponents/DropdownAndModalsWithIsFavorite/Modal/Delete/DeleteItemModal";
import GridRow from "@src/sharedComponents/GridRow/GridRow";
import DataProcessingModal from "@src/sharedComponents/Modal/DataProcessingModal/DataProcessingModal";
import SimpleModalWithTextInput from "@src/sharedComponents/Modal/SimpleModalWithTextInput/SimpleModalWithTextInput";
import TitleBlock from "@src/sharedComponents/PageHero/TitleBlock";
import PaginatedTable from "@src/sharedComponents/Table/PaginatedTable";
import Text from "@src/sharedComponents/Typography/Text";
import { State } from "@src/store";
import { formatRelativeDateString } from "@src/utils/formatRelativeDateString";
import { handleApiError } from "@src/utils/handleApiError";

import {
  advisorColumns,
  clientColumns,
  proposalCardButtons,
} from "./constants";
import { strings } from "./strings";
import { TransitionProposalActionMenu } from "./TransitionProposalActionMenu/TransitionProposalActionMenu";
import TransitionProposalChipStatusProcessingPolling from "./TransitionProposalChipStatusProcessingPolling/TransitionProposalChipStatusProcessingPolling";
import ConfirmShareProposalModal from "./TransitionProposalFlowSteps/Share/ConfirmShareProposalModal/ConfirmShareProposalModal";
import {
  CreateProposalButtonAction,
  MenuActions,
  TransitionProposalsTableColumnValue,
} from "./types";

import styles from "./TransitionProposals.module.css";

const GRID_AUTO_ROWS = "256px";

interface LocationState {
  processing?: boolean;
}

type ActionModalType = TransitionProposalsImportTypeKey | MenuActions;

const TransitionProposals: React.FC = () => {
  const { isAdvisor } = useSelector((state: State) => state.main_Reducer.user);

  const location = useLocation<LocationState>();
  const navigate = useNavigate();

  const { clientId } = useParams<{ clientId: ClientId }>();
  const [page, setPage] = useState(1);
  const [pageSize, _setPageSize] = useState(10);

  const [actionModal, setActionModal] = useState<ActionModalType | null>(null);
  const [proposalIsProcessing, setProposalIsProcessing] = useState(false);

  const [proposalForAction, setProposalForAction] = useState<
    TransitionProposal | undefined
  >(undefined);

  const auth = useRequestAuth();

  const notify = useStatusNotification();

  const { stopPolling } = useGlobalPollingContext();

  const showDataProcessingModal = useMemo(() => {
    if (proposalIsProcessing) {
      return true;
    }

    if (location.search === undefined || location.search === "") {
      return false;
    }

    const queryParams = new URLSearchParams(location.search);

    const processingParam = queryParams.get("processing");

    const processing: boolean = processingParam === "true";

    return processing;
  }, [location, proposalIsProcessing]);

  const clientProfile = useGetClient(clientId, auth);

  const clientFullName = useMemo(() => {
    if (clientProfile.data === undefined) {
      return "";
    }

    const { first: firstName = "", last: lastName = "" } =
      clientProfile.data.name ?? {};

    return lastName ? `${firstName} ${lastName}` : firstName;
  }, [clientProfile.data]);

  const { data, isLoading: isFetchingTransitionProposals } =
    useGetTransitionProposals(
      {
        page,
        pageSize,
        orderBy: "createdAt desc",
        clientId,
        status: isAdvisor === false ? "shared" : undefined,
      },
      auth
    );

  const {
    mutateAsync: createTransitionProposal,
    isLoading: isMutatingPostProposal,
  } = usePostTransitionProposal(auth);

  const { mutateAsync: duplicateTransitionProposal } =
    usePostDuplicateTransitionProposal(auth);

  const { mutateAsync: updateTransitionProposal } =
    usePatchTransitionProposal(auth);

  const { mutateAsync: deleteTransitionProposal } =
    useDeleteTransitionProposal(auth);

  const {
    mutateAsync: shareTransitionProposal,
    isLoading: isMutatingShareTransitionProposal,
  } = usePostShareTransitionProposal(auth);

  const handleOnDuplicateProposal = useCallback(
    async (transitionProposalId: TransitionProposalId) => {
      try {
        const response = await duplicateTransitionProposal({
          body: {},
          transitionProposalId,
        });

        // Track duplicate event
        if (response?.data?.transitionProposalId) {
          trackEvent({
            name: "Advisor Duplicate Transition-Proposal",
            properties: {
              sourceProposalId: transitionProposalId,
              newProposalId: response.data.transitionProposalId,
            },
          });
        }

        notify(strings.PROPOSAL_DUPLICATED, "Success");
      } catch (error) {
        handleApiError({
          error,
          errorMessage: strings.ERROR_DUPLICATING_PROPOSAL,
          genericErrorMessage: strings.ERROR_DUPLICATING_PROPOSAL_GENERIC,
          notify,
        });
      } finally {
        setActionModal(null);
      }
    },
    [duplicateTransitionProposal, notify, setActionModal]
  );

  const handleOnClickAction = useCallback(
    async (action: MenuActions, proposal: TransitionProposal) => {
      if (action === MenuActions.Duplicate) {
        await handleOnDuplicateProposal(proposal.transitionProposalId);
      }
      setProposalForAction(proposal);
      setActionModal(action);
    },
    [handleOnDuplicateProposal, setProposalForAction, setActionModal]
  );

  const handleOnNavigateToProposalAsAdvisor = useCallback(
    (proposal: TransitionProposal) => () => {
      if (proposal.status === "processing") {
        setProposalIsProcessing(proposal.status === "processing");
        return;
      }

      // Track viewing proposal details
      trackEvent({
        name: "Advisor View Transition-Proposal Details",
        properties: {
          proposalId: proposal.transitionProposalId,
          proposalName: proposal.name,
        },
      });

      const pathname =
        proposal.importType === "statement"
          ? `/Client/${clientId}/Investments/TransitionProposals/${proposal.transitionProposalId}/Statements`
          : `/Client/${clientId}/Investments/TransitionProposals/${proposal.transitionProposalId}`;

      navigate({ ...location, pathname });
    },
    [clientId, navigate, location]
  );

  const handleOnNavigateToProposalAsClient = useCallback(
    (proposal: TransitionProposal) => async () => {
      // Track client viewing proposal
      trackEvent({
        name: "Client View Transition-Proposal",
        properties: {
          proposalId: proposal.transitionProposalId,
          proposalName: proposal.name,
        },
      });
      const pathname = `/Client/${clientId}/Investments/TransitionProposals/${proposal.transitionProposalId}/Compare`;
      navigate({ ...location, pathname });
      if (proposal.isRead === false) {
        const body = { isRead: true };
        try {
          await updateTransitionProposal({
            transitionProposalId: proposal.transitionProposalId,
            body,
          });
        } catch (error) {
          console.error(
            "Failed to set read status on transition proposal",
            error
          );
        }
      }
    },
    [clientId, location, navigate, updateTransitionProposal]
  );

  const handleOnClickCardButton =
    (action: CreateProposalButtonAction["type"]) => () => {
      setActionModal(action);
    };

  const handleOnCreateProposalName = useCallback(
    async (name: string) => {
      const importType = actionModal as TransitionProposalsImportTypeKey;

      const body = { clientId, name, importType };

      try {
        const res = await createTransitionProposal({ body });

        // Track the creation event
        trackEvent({
          name: "Advisor Create Transition-Proposal",
          properties: {
            name,
            importType,
          },
        });

        // Track the flow initiation event
        trackEvent({
          name: "Advisor Initiate Transition-Proposal Flow",
          properties: {
            flowType: importType,
          },
        });

        handleOnNavigateToProposalAsAdvisor(res.data)();

        notify(strings.PROPOSAL_CREATED, "Success");
      } catch (error) {
        handleApiError({
          error,
          errorMessage: strings.ERROR_CREATING_PROPOSAL,
          genericErrorMessage: strings.ERROR_CREATING_PROPOSAL_GENERIC,
          notify,
        });
      } finally {
        setActionModal(null);
      }
    },
    [
      actionModal,
      createTransitionProposal,
      handleOnNavigateToProposalAsAdvisor,
      notify,
      clientId,
    ]
  );

  const handleOnUpdateProposalName = async (name: string) => {
    if (proposalForAction === undefined) {
      throw new Error(strings.PROPOSAL_TO_UPDATE_UNDEFINED);
    }
    const body = { clientId, name };
    const oldName = proposalForAction.name;

    try {
      await updateTransitionProposal({
        transitionProposalId: proposalForAction.transitionProposalId,
        body,
      });

      // Track rename event
      trackEvent({
        name: "Advisor Rename Transition-Proposal",
        properties: {
          proposalId: proposalForAction.transitionProposalId,
          oldName,
          newName: name,
        },
      });

      notify(strings.PROPOSAL_CREATED, "Success");
    } catch (error) {
      handleApiError({
        error,
        errorMessage: strings.ERROR_CREATING_PROPOSAL,
        genericErrorMessage: strings.ERROR_CREATING_PROPOSAL_GENERIC,
        notify,
      });
    } finally {
      setActionModal(null);
    }
  };

  const handleOnDeleteProposal = async () => {
    if (proposalForAction === undefined) {
      throw new Error(strings.PROPOSAL_TO_DELETE_UNDEFINED);
    }
    try {
      await deleteTransitionProposal({
        transitionProposalId: proposalForAction.transitionProposalId,
      });

      // Track delete event
      trackEvent({
        name: "Advisor Delete Transition-Proposal",
        properties: {
          proposalId: proposalForAction.transitionProposalId,
          proposalName: proposalForAction.name,
        },
      });

      stopPolling<TransitionProposalId>(proposalForAction.transitionProposalId);

      notify(strings.PROPOSAL_DELETED, "Success");
    } catch (error) {
      handleApiError({
        error,
        errorMessage: strings.ERROR_DELETING_PROPOSAL,
        genericErrorMessage: strings.ERROR_DELETING_PROPOSAL_GENERIC,
        notify,
      });
    } finally {
      setActionModal(null);
    }
  };

  const handleConfirmShareTransitionProposal = useCallback(async () => {
    if (proposalForAction?.transitionProposalId === undefined) {
      return;
    }

    const transitionProposalId = proposalForAction.transitionProposalId;

    try {
      await shareTransitionProposal({ body: {}, transitionProposalId });

      // Track sharing proposal
      trackEvent({
        name: "Advisor Share Transition-Proposal",
        properties: {
          proposalId: transitionProposalId,
          clientId,
        },
      });

      // Track status change from current status to "shared"
      trackEvent({
        name: "Advisor Change Transition-Proposal Status",
        properties: {
          proposalId: transitionProposalId,
          oldStatus: proposalForAction.status,
          newStatus: "shared",
        },
      });

      notify(strings.PROPOSAL_SHARED, "Success");
    } catch (error) {
      handleApiError({
        error,
        errorMessage: strings.ERROR_SHARING_PROPOSAL,
        genericErrorMessage: strings.ERROR_SHARING_PROPOSAL_GENERIC,
        notify: notify,
      });
    } finally {
      setActionModal(null);
    }
  }, [
    shareTransitionProposal,
    notify,
    proposalForAction,
    setActionModal,
    clientId,
  ]);

  const handleOnCloseActionModal = () => {
    setActionModal(null);
  };

  const columnStyles = (accessor: TransitionProposalsTableColumnValue) => {
    if (accessor === "action") {
      return { className: styles.actionCell };
    }
  };

  const showCreateModal = useMemo(() => {
    if (actionModal === null) {
      return false;
    }
    const isCreateProposal = Object.keys(
      TransitionProposalsImportTypeMap
    ).includes(actionModal);
    return isCreateProposal;
  }, [actionModal]);

  const tableRowData = useMemo(() => {
    const transitionProposals = data?.data;

    if (isFetchingTransitionProposals || transitionProposals === undefined) {
      return [];
    }
    return transitionProposals.map((proposal) => ({
      key: proposal.transitionProposalId,
      name: {
        value: (
          <div>
            {proposal.name}
            {proposal.isRead === false && !isAdvisor && (
              <Text
                variant="bodySmall"
                className={styles.unreadStatusIndicator}
              >
                New
              </Text>
            )}
          </div>
        ),
      },
      onClick: isAdvisor
        ? handleOnNavigateToProposalAsAdvisor(proposal)
        : handleOnNavigateToProposalAsClient(proposal),
      status: {
        value:
          proposal.status === "processing" ? (
            <TransitionProposalChipStatusProcessingPolling
              transitionProposalId={proposal.transitionProposalId}
            />
          ) : (
            <Chip
              label={TransitionProposalsStatusMap[proposal.status]}
              variant="outlined"
            />
          ),
      },
      updatedAt: formatRelativeDateString(proposal.updatedAt),
      action: {
        value: (
          <TransitionProposalActionMenu
            proposal={proposal}
            onClick={handleOnClickAction}
          />
        ),
      },
    }));
  }, [
    data?.data,
    isFetchingTransitionProposals,
    isAdvisor,
    handleOnNavigateToProposalAsAdvisor,
    handleOnNavigateToProposalAsClient,
    handleOnClickAction,
  ]);

  const pagination = useMemo(() => {
    if (data?.pagination === undefined) {
      return {
        currentPage: page,
        totalPages: 1,
        pageSize,
        totalCount: 0,
        prevPage: undefined,
        nextPage: undefined,
      };
    }

    const totalPages = Math.ceil(
      data.pagination.totalCount / data.pagination.pageSize
    );

    return {
      currentPage: page,
      totalPages,
      pageSize: data.pagination.pageSize,
      totalCount: data.pagination.totalCount,
      prevPage: page === 1 ? undefined : page - 1,
      nextPage: page === totalPages ? undefined : page + 1,
    };
  }, [data, page, pageSize]);

  const columns = isAdvisor ? advisorColumns : clientColumns;

  const pageTitle = isAdvisor ? strings.PAGE_TITLE_ADVISOR : strings.PAGE_TITLE;
  const pageSubtitle = isAdvisor
    ? strings.PAGE_SUBTITLE_ADVISOR
    : strings.PAGE_SUBTITLE;

  const handleOnCloseDataProcessingModal = () => {
    navigate({
      pathname: location.pathname,
      search: "",
    });

    setProposalIsProcessing(false);
  };

  const handleOnCloseShareModal = () => {
    setActionModal(null);
  };

  return (
    <Fragment>
      <TitleBlock
        className={styles.bodyTitleBlock}
        titleVariant="title2"
        title={pageTitle}
        subTitle={pageSubtitle}
      />
      {isAdvisor && (
        <GridRow
          className={styles.gridRow}
          gridAutoRows={GRID_AUTO_ROWS}
          maxColumns={3}
        >
          {proposalCardButtons.map((b, idx) => (
            <CardButton
              key={idx}
              header={b.title}
              onClick={handleOnClickCardButton(b.type)}
            >
              <Text color="textSubtle" variant="body">
                {b.description}
              </Text>
            </CardButton>
          ))}
        </GridRow>
      )}

      <PaginatedTable
        isLoading={isFetchingTransitionProposals}
        columns={columns}
        rows={tableRowData}
        columnStyles={columnStyles}
        rowClassName={styles.tableRow}
        pagination={pagination}
        showEmptyRows={isFetchingTransitionProposals}
        onPageChange={(newPage) => setPage(newPage)}
      />

      {showCreateModal && (
        <SimpleModalWithTextInput
          title={strings.NEW_PROPOSAL}
          label={strings.PROPOSAL_NAME}
          confirmText={strings.CONFIRM_CONTINUE}
          defaultTextInputValue=""
          placeholder={strings.PROPOSAL_PLACEHOLDER}
          onSave={handleOnCreateProposalName}
          closeModal={handleOnCloseActionModal}
          isDisabled={isMutatingPostProposal}
        />
      )}

      {actionModal === MenuActions.Rename && (
        <SimpleModalWithTextInput
          title={strings.RENAME_PROPOSAL}
          label={strings.PROPOSAL_NAME}
          confirmText={strings.CONFIRM_RENAME}
          defaultTextInputValue={proposalForAction?.name ?? ""}
          placeholder={strings.PROPOSAL_PLACEHOLDER}
          onSave={handleOnUpdateProposalName}
          closeModal={handleOnCloseActionModal}
          isDisabled={false}
        />
      )}

      {proposalForAction !== undefined && (
        <ConfirmShareProposalModal
          isOpen={MenuActions.Share === actionModal}
          onClose={handleOnCloseShareModal}
          onConfirmShare={handleConfirmShareTransitionProposal}
          isLoading={isMutatingShareTransitionProposal}
          transitionProposal={proposalForAction}
          clientFullName={clientFullName}
        />
      )}

      {actionModal === MenuActions.Delete &&
        proposalForAction !== undefined && (
          <DeleteItemModal
            title={strings.DELETE_PROPOSAL}
            text={strings.DELETE_PROPOSAL_TEXT.replace(
              "{proposalName}",
              proposalForAction.name
            )}
            deleteButtonText={strings.CONFIRM_DELETE}
            onDelete={handleOnDeleteProposal}
            onClose={handleOnCloseActionModal}
          />
        )}

      {showDataProcessingModal && (
        <DataProcessingModal
          headerTitle={strings.DATA_PROCESSING_HEADER_TITLE}
          headerSubTitle={strings.DATA_PROCESSING_HEADER_SUBTITLE}
          footerTitle={strings.DATA_PROCESSING_FOOTER_TITLE}
          footerSubTitle={strings.DATA_PROCESSING_FOOTER_SUBTITLE}
          onClose={handleOnCloseDataProcessingModal}
        />
      )}
    </Fragment>
  );
};

export default TransitionProposals;
